import { useState } from 'react';
import FileUpload from 'components/FileUpload';
import DownloadBox from 'components/DownloadBox';
import Icon from 'components/Icon';
import Seo from 'components/Seo';
import { toolAPI, mkUrl } from 'utils/api';
import ToolSeoContent from 'components/ToolSeoContent';

function fmtSize(bytes) {
  if (!bytes || bytes <= 0) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024*1024) return (bytes/1024).toFixed(1) + ' KB';
  return (bytes/(1024*1024)).toFixed(2) + ' MB';
}

export default function PDFCompress() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dlUrl, setDlUrl] = useState('');
  const [stats, setStats] = useState(null);

  const reset = () => { setFile(null); setError(''); setDlUrl(''); setStats(null); };

  const handle = async () => {
    if (!file) { setError('Please select a PDF file.'); return; }
    setLoading(true); setError(''); setDlUrl(''); setStats(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await toolAPI.compressPDF(fd);
      const origSize = parseInt(res.headers['x-original-size']   || '0');
      const compSize = parseInt(res.headers['x-compressed-size'] || '0');
      const savedPct = parseInt(res.headers['x-saved-percent']   || '0');
      if (origSize && compSize) {
        setStats([
          { value: fmtSize(origSize), label: 'Original Size' },
          { value: fmtSize(compSize), label: 'New Size' },
          { value: savedPct + '%',    label: 'Size Reduced' },
        ]);
      }
      setDlUrl(mkUrl(res.data));
    } catch (err) {
      setError(err.response?.data?.error || 'Compression failed. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <>
    <Seo title="Compress PDF — Reduce File Size Free" description="Compress PDF files to reduce size while keeping quality. Free, no signup." path="/tools/pdf/compress" />
    <div className="tool-pg">
      <div className="container" style={{ maxWidth: 720 }}>
        <div className="tool-hdr">
          <div className="tool-hdr-row">
            <div className="tool-hdr-icon"><Icon name="compress" size={24} /></div>
            <div>
              <h1>PDF Compressor</h1>
              <p>Reduce your PDF file size instantly. Shows exact size reduction after processing.</p>
            </div>
          </div>
        </div>
        {!dlUrl ? (
          <div className="tool-box">
            <FileUpload accept=".pdf,application/pdf" onFileChange={setFile} hint="PDF files only · Max 50MB" />
            {file && (
              <div className="alert alert-info" style={{ marginTop:14 }}>
                <Icon name="info" size={16} />
                <span>Selected: <strong>{file.name}</strong> — Current size: <strong>{fmtSize(file.size)}</strong></span>
              </div>
            )}
            {error && <div className="alert alert-error" style={{ marginTop:14 }}><Icon name="alertCircle" size={16} />{error}</div>}
            <button className="btn btn-primary btn-lg btn-full" style={{ marginTop:20 }} onClick={handle} disabled={loading || !file}>
              {loading ? <><span className="spinner sp-sm" />Compressing PDF...</> : <><Icon name="compress" size={18} />Compress PDF</>}
            </button>
          </div>
        ) : (
          <DownloadBox url={dlUrl} filename="compressed.pdf" onReset={reset} stats={stats} />
        )}
        <div className="alert alert-info" style={{ marginTop:14 }}>
          <Icon name="info" size={16} />
          <span>Compression removes metadata and optimizes PDF structure. Typical reduction is 10–40% for most PDFs.</span>
        </div>
        <ToolSeoContent tool="pdf-compress" />
      </div>
    </div>
  </>
  );
}
